import type {
  FieldMapping,
  SyncBehaviorConfig,
  SyncScheduleConfig,
} from "@attendee-sync/connectors";
import { defaultBehavior } from "@attendee-sync/connectors";

export const WIZARD_KEY = "rs-wizard";

export type WizardEvent = {
  id: string;
  resourceType: string;
  name: string;
  startTime?: string | null;
  connectionId?: string;
};

export type WizardConfigureState = {
  schedule: SyncScheduleConfig;
  mappings: FieldMapping[];
  behavior: SyncBehaviorConfig;
  /** `${source}:${destination}:${resourceId}:${credentialId}` when mappings were last reconciled. */
  schemaKey?: string;
  /** Last live field-discovery poll metadata (schemas themselves are re-fetched). */
  discovery?: {
    discoveredAt?: string;
    sourceDiscovery?: "live" | "static";
    destinationDiscovery?: "live" | "static";
    warnings?: string[];
  };
};

export type WizardSourceId =
  | "zoom"
  | "google_meet"
  | "google_forms"
  | "linkedin_events"
  | "heysummit"
  | "fluent_forms"
  | "constant_contact"
  | "mailchimp"
  | "eventbrite"
  | "microsoft_teams";

export type WizardState = {
  streamName: string;
  /** Confirmed on Setup Source chooser — drives connect / event sub-phases. */
  source?: WizardSourceId;
  /** True when the selected source has an active OAuth connection. */
  sourceConnected: boolean;
  event?: WizardEvent;
  /** Confirmed on Setup Destinations chooser — drives credential form sub-phase. */
  destinationProvider?: string;
  destinationConfigured: boolean;
  destinationCredentialId?: string | null;
  configure?: WizardConfigureState;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseConfigure(raw: unknown): WizardConfigureState | undefined {
  if (!isRecord(raw)) return undefined;
  if (
    !isRecord(raw.schedule) ||
    !Array.isArray(raw.mappings) ||
    !isRecord(raw.behavior)
  ) {
    return undefined;
  }
  return {
    schedule: raw.schedule as unknown as SyncScheduleConfig,
    mappings: raw.mappings as FieldMapping[],
    behavior: {
      ...defaultBehavior(),
      ...(raw.behavior as unknown as SyncBehaviorConfig),
    },
    schemaKey: typeof raw.schemaKey === "string" ? raw.schemaKey : undefined,
    discovery: isRecord(raw.discovery)
      ? {
          discoveredAt:
            typeof raw.discovery.discoveredAt === "string"
              ? raw.discovery.discoveredAt
              : undefined,
          sourceDiscovery:
            raw.discovery.sourceDiscovery === "live" ||
            raw.discovery.sourceDiscovery === "static"
              ? raw.discovery.sourceDiscovery
              : undefined,
          destinationDiscovery:
            raw.discovery.destinationDiscovery === "live" ||
            raw.discovery.destinationDiscovery === "static"
              ? raw.discovery.destinationDiscovery
              : undefined,
          warnings: Array.isArray(raw.discovery.warnings)
            ? raw.discovery.warnings.filter(
                (w): w is string => typeof w === "string",
              )
            : undefined,
        }
      : undefined,
  };
}

export function loadWizard(orgId: string): WizardState {
  try {
    const raw = sessionStorage.getItem(`${WIZARD_KEY}:${orgId}`);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<WizardState> & {
        zoomConnected?: boolean;
      };
      const source =
        parsed.source === "zoom" ||
        parsed.source === "google_meet" ||
        parsed.source === "google_forms" ||
        parsed.source === "linkedin_events" ||
        parsed.source === "heysummit" ||
        parsed.source === "fluent_forms" ||
        parsed.source === "constant_contact" ||
        parsed.source === "mailchimp" ||
        parsed.source === "eventbrite" ||
        parsed.source === "microsoft_teams"
          ? parsed.source
          : undefined;
      return {
        streamName:
          typeof parsed.streamName === "string" ? parsed.streamName : "",
        source,
        sourceConnected: Boolean(
          parsed.sourceConnected ?? parsed.zoomConnected,
        ),
        event: parsed.event,
        destinationProvider:
          typeof parsed.destinationProvider === "string" &&
          parsed.destinationProvider.trim()
            ? parsed.destinationProvider
            : undefined,
        destinationConfigured: Boolean(parsed.destinationConfigured),
        destinationCredentialId:
          typeof parsed.destinationCredentialId === "string"
            ? parsed.destinationCredentialId
            : parsed.destinationCredentialId === null
              ? null
              : undefined,
        configure: parseConfigure(parsed.configure),
      };
    }
  } catch {
    /* ignore */
  }
  return {
    streamName: "",
    sourceConnected: false,
    destinationConfigured: false,
  };
}

export function saveWizard(orgId: string, state: WizardState) {
  sessionStorage.setItem(`${WIZARD_KEY}:${orgId}`, JSON.stringify(state));
}

export function clearWizard(orgId: string) {
  sessionStorage.removeItem(`${WIZARD_KEY}:${orgId}`);
}

export function configureSchemaKey(
  source?: string,
  destination?: string,
  resourceId?: string,
  credentialId?: string | null,
): string {
  return `${source ?? ""}:${destination ?? ""}:${resourceId ?? ""}:${credentialId ?? ""}`;
}
